import { useEffect, useMemo, useState } from "react";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import type { AudienceDefinition } from "@models/audienceDefinition.model";

/*
    Who a campaign's sequences send to, and how many of them (MPN-2204).

    Audiences belong to the engagement, so the list is engagement-scoped; the ones carrying
    this campaign's `launch_id` are what "use the campaign audience" means. The counts used
    to be five invented options and a hardcoded 3,960, which made the footer's cost estimate
    the same number on every campaign in the platform.

    `total_targetable` rather than `total_members`: the estimate is for people who will be
    sent to, and a holdout is not one of them.
*/
export function useCampaignAudiences(engagementId: number | null, launchId: number | null) {
  const [audiences, setAudiences] = useState<AudienceDefinition[]>([]);

  useEffect(() => {
    if (!engagementId) {
      setAudiences([]);
      return;
    }
    let cancelled = false;
    new AudienceDefinitionService()
      .listAudiences(engagementId)
      .then((loaded) => {
        if (!cancelled) setAudiences(loaded ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  return useMemo(() => {
    const options = audiences.map((audience) => ({
      v: String(audience.id),
      label: `${audience.name} | ${audience.total_targetable.toLocaleString()}`,
    }));

    const mine = audiences.filter((audience) => audience.launch_id === launchId);
    const campaignReach = mine.reduce((sum, a) => sum + a.total_targetable, 0);
    const campaignAudienceLabel = mine.length
      ? `${mine.map((a) => a.name).join(", ")} | ${campaignReach.toLocaleString()}`
      : "No audience on this campaign yet";

    const reachById = Object.fromEntries(
      audiences.map((a) => [a.id, a.total_targetable]),
    ) as Record<number, number>;

    return {
      options,
      campaignAudienceLabel,
      /*
          The same people as email until consent gating exists. Per-school SMS registration
          and consent-gated audiences are MPN-2212, and there is no consent field on any
          model yet, so a separate SMS figure here could only be invented.
      */
      smsAudienceLabel: campaignAudienceLabel,
      campaignReach,
      reachById,
    };
  }, [audiences, launchId]);
}
